import AddToCalendar from '@liveauctioneers/caterwaul-components/lib/AddToCalendar/AddToCalendar';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback } from 'react';
import styled from 'styled-components';

import RequestOnlineBiddingButton from '@/components/ItemBidding/RequestOnlineBiddingButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogDescription,
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailExternalUrl } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { submitRegisterForCatalog, SubmitRegisterForCatalogParams } from '@/redux/modules/registerForCatalog';
import { useTrackAddToCalendarPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackAddToCalendarPressed.analytics';
import { calendarEvent } from '@/utils/calendarEvent';

import CatalogRegistrationMessages from './CatalogRegistrationMessages';

type Props = {
    catalogId: number;
    className?: string;
};

const CatalogOnlyAuctionStatus = ({ catalogId, className }: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const externalUrl = useAppSelector((state) => getCatalogDetailExternalUrl(state, catalogId));
    const trackAddToCalendarPressedAnalytics = useTrackAddToCalendarPressedAnalytics();

    const registerForCatalog = useCallback(
        ({ catalogId, itemId }: SubmitRegisterForCatalogParams) =>
            dispatch(submitRegisterForCatalog({ catalogId, itemId })),
        [dispatch]
    );

    const trackAddToCalendar = (calendar: string, calendarType: string) => {
        trackAddToCalendarPressedAnalytics({ calendarType });
    };

    const event = calendarEvent({
        catalogId,
        description,
        saleStartTs,
        sellerName,
        title,
    });

    return (
        <StyledCatalogOnlyAuctionStatus
            className={className}
            data-testid="catalog-only-auction-status"
        >
            <CatalogRegistrationMessages catalogId={catalogId} />
            <StyledRegisterButton
                catalogId={catalogId}
                submitRegisterForCatalog={registerForCatalog}
                text={Boolean(externalUrl) && <FormattedMessage id="visit_partners_site" />}
            />
            <AddToCalendarButton
                buttonLabel={formatMessage({ id: 'add_to_calendar' })}
                event={event}
                onClick={trackAddToCalendar}
            />
        </StyledCatalogOnlyAuctionStatus>
    );
};

export default CatalogOnlyAuctionStatus;

const StyledCatalogOnlyAuctionStatus = styled.div`
    height: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
`;

const StyledRegisterButton = styled(RequestOnlineBiddingButton)`
    margin-bottom: 16px;
    width: 100%;
    max-width: 100%;
`;

const AddToCalendarButton = styled(AddToCalendar)`
    position: relative;
    width: 100%;
    max-width: 100%;

    & div > button {
        margin-bottom: 0;
        width: 100%;
    }
`;
